import { getPage } from "../api/page.js";
import type { Credentials } from "./credentials.js";
import {
  fetchBlobFromUrl,
  findPageAttachmentByResourceHash,
  findPageImageUrlByResourceHash,
  formatPageAsMarkdown,
} from "./formatters/page.js";

export type PageResourceType = "image" | "attachment";

export interface PageResourceRequest {
  pageId: number;
  resourceHash: string;
  resourceType: PageResourceType;
}

export type PageResourceResult =
  | {
      ok: true;
      blob: string;
      mimeType: string;
      originalUrl: string;
    }
  | {
      ok: false;
      kind: "page_not_found" | "resource_not_found" | "fetch_failed";
      message: string;
    };

export async function fetchPageResource(
  credentials: Credentials,
  pageId: number,
  resourceHash: string,
  resourceType: PageResourceType,
  clientUserAgent?: string,
): Promise<PageResourceResult> {
  const page = await getPage(credentials, pageId, {}, clientUserAgent);

  if (!page) {
    return {
      ok: false,
      kind: "page_not_found",
      message: `Could not find the page for this ${resourceType} resource.`,
    };
  }

  return fetchPageResourceFromContent(
    formatPageAsMarkdown(page),
    resourceHash,
    resourceType,
  );
}

export async function fetchPageResources(
  credentials: Credentials,
  requests: PageResourceRequest[],
  clientUserAgent?: string,
): Promise<PageResourceResult[]> {
  const pageContentById = new Map<number, Promise<string | null>>();
  const getPageContent = (pageId: number) => {
    if (!pageContentById.has(pageId)) {
      pageContentById.set(
        pageId,
        getPage(credentials, pageId, {}, clientUserAgent).then((page) =>
          page ? formatPageAsMarkdown(page) : null,
        ),
      );
    }

    return pageContentById.get(pageId)!;
  };

  return Promise.all(
    requests.map(async (request) => {
      const pageContent = await getPageContent(request.pageId);
      if (!pageContent) {
        return {
          ok: false,
          kind: "page_not_found",
          message: `Could not find the page for this ${request.resourceType} resource.`,
        };
      }

      return fetchPageResourceFromContent(
        pageContent,
        request.resourceHash,
        request.resourceType,
      );
    }),
  );
}

async function fetchPageResourceFromContent(
  pageContent: string,
  resourceHash: string,
  resourceType: PageResourceType,
): Promise<PageResourceResult> {
  const imageUrl =
    resourceType === "image"
      ? findPageImageUrlByResourceHash(pageContent, resourceHash)
      : null;
  const attachment =
    resourceType === "attachment"
      ? findPageAttachmentByResourceHash(pageContent, resourceHash)
      : null;
  const resource = resourceType === "image" ? imageUrl : attachment?.url;

  if (!resource) {
    return {
      ok: false,
      kind: "resource_not_found",
      message: `Could not find this ${resourceType} on the page.`,
    };
  }

  const blob = await fetchBlobFromUrl(resource);
  if (!blob) {
    return {
      ok: false,
      kind: "fetch_failed",
      message: `Could not fetch ${resourceType}: ${resource}`,
    };
  }

  return {
    ok: true,
    blob: blob.blob,
    mimeType: attachment?.type || blob.mimeType,
    originalUrl: resource,
  };
}
